import { useId, type ReactNode } from "react";

export interface FieldIds {
  id: string;
  describedBy: string | undefined;
}

export interface FieldProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** Receives the ids to put on the control: id for the label, aria-describedby for hint and error. */
  children: (ids: FieldIds) => ReactNode;
}

/** Label, optional hint and error around one control. Errors name the problem in plain words. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-note font-semibold text-ink-2">
        {label}
      </label>
      {children({ id, describedBy })}
      {hint ? (
        <p id={hintId} className="text-micro text-ink-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-note font-medium text-red">
          {error}
        </p>
      ) : null}
    </div>
  );
}
